import React, { useState, useContext } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Player from './components/Player';
import AdBanner from './components/AdBanner';
import Home from './pages/Home';
import VideoDetail from './pages/ShowDetail';
import './App.css';

export const PlayerContext = React.createContext();

/* ── Mobile top bar ── */
function MobTopbar({ onSearchOpen }) {
  return (
    <header className="mob-topbar">
      <Link to="/" className="mob-topbar-logo">
        <img src="/shrifm.png" alt="Shri FM" className="mob-topbar-logo-img" />
        <span className="mob-topbar-title">Shri<span>FM</span></span>
      </Link>
      <button className="mob-topbar-search" onClick={onSearchOpen} aria-label="Search">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
      </button>
    </header>
  );
}

/* ── Slide-down search bar ── */
function MobSearchBar({ onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/?view=search&q=${encodeURIComponent(q.trim())}`); onClose(); }
  };
  return (
    <div className="mob-search-bar">
      <form onSubmit={submit} style={{ display: 'flex', flex: 1, gap: 10, alignItems: 'center' }}>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search music…" />
      </form>
      <button className="mob-search-cancel" onClick={onClose}>Cancel</button>
    </div>
  );
}

/* ── Bottom nav with raised centre play button ── */
function MobNav({ onPlayPress }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const view = new URLSearchParams(search).get('view');
  const { currentTrack } = useContext(PlayerContext);

  const is     = (v) => pathname === '/' && view === v;
  const isHome = pathname === '/' && !view;

  return (
    <nav className="mob-nav">
      {/* Home */}
      <button className={`mob-nav-btn ${isHome ? 'active' : ''}`} onClick={() => navigate('/')}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
        </svg>
        <span>Home</span>
      </button>

      {/* Music */}
      <button className={`mob-nav-btn ${is('music') ? 'active' : ''}`} onClick={() => navigate('/?view=music')}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3v10.55A4 4 0 1014 17V7h4V3h-6z"/>
        </svg>
        <span>Music</span>
      </button>

      {/* Centre play button */}
      <button className="mob-nav-play" onClick={onPlayPress} aria-label="Player">
        <div className="mob-nav-play-inner">
          {currentTrack
            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
              </svg>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                <polygon points="6,3 20,12 6,21"/>
              </svg>
          }
        </div>
        <span style={{ fontSize: '0.52rem', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: currentTrack ? 'var(--accent)' : 'var(--text2)' }}>Now</span>
      </button>

      {/* Radio */}
      <button className={`mob-nav-btn ${is('radio') ? 'active' : ''}`} onClick={() => navigate('/?view=radio')}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.24 6.15C2.51 6.43 2 7.17 2 8v12c0 1.1.89 2 2 2h16c1.11 0 2-.9 2-2V8c0-1.11-.89-2-2-2H5.43L3.24 6.15zM11 20c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm8-12h-2v2h-2v-2H6V6h5.44L4.42 0 2.99 1.39 5.43 4H2C.9 4 0 4.89 0 6v14c0 1.11.9 2 2 2h1v-2H2V6h3v2h2V6h6v2h2V6h2v2z"/>
        </svg>
        <span>Radio</span>
      </button>

      {/* New */}
      <button className={`mob-nav-btn ${is('new') ? 'active' : ''}`} onClick={() => navigate('/?view=new')}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
        </svg>
        <span>New</span>
      </button>
    </nav>
  );
}

function AppShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  const { currentTrack, setMobPlayerOpen } = useContext(PlayerContext);

  const handlePlayPress = () => {
    if (currentTrack) setMobPlayerOpen(true);
  };

  return (
    <>
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile top bar */}
      <MobTopbar onSearchOpen={() => setSearchOpen(true)} />
      {searchOpen && <MobSearchBar onClose={() => setSearchOpen(false)} />}

      {/* Main content */}
      <div className="layout">
        <div className={`page-body${searchOpen ? ' search-open' : ''}`}>
          <Routes>
            <Route path="/"         element={<Home />} />
            <Route path="/show/:id" element={<VideoDetail />} />
          </Routes>
        </div>
      </div>

      {/* Player (mini bar + full-screen overlay both live inside Player.js) */}
      <Player />

      {/* Mobile bottom nav */}
      <MobNav onPlayPress={handlePlayPress} />

      <AdBanner />
    </>
  );
}

export default function App() {
  const [currentTrack, setCurrentTrack]   = useState(null);
  const [queue, setQueue]                 = useState([]);
  const [queueIdx, setQueueIdx]           = useState(0);
  const [mobPlayerOpen, setMobPlayerOpen] = useState(false);
  return (
    <PlayerContext.Provider value={{ currentTrack, setCurrentTrack, queue, setQueue, queueIdx, setQueueIdx, mobPlayerOpen, setMobPlayerOpen }}>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </PlayerContext.Provider>
  );
}
